import type { PropsWithChildren, ReactNode } from 'react';

const MIN_CONTENT_HEIGHT_PX = 580;
const BACKGROUND_BLUR_PX = 5;
const BACKGROUND_BLUR_SCALE = 1.03;
const BACKGROUND_GRADIENT_OVERLAY = `linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.65) 100%), linear-gradient(218deg, rgba(255, 170, 145, 0.92) 5%, rgba(151, 71, 255, 0.78) 89.54%)`;

export type SurveyWindowProps = PropsWithChildren<{
    backgroundImageUrl?: string;
    /** Closes the dark card. */
    cardBottom: ReactNode;
    /** Sits under the dark card. */
    footer?: ReactNode;
    className?: string;
}>;

/** The glass window of a survey: its dark card and what sits under it, whatever the card shows. */
export const SurveyWindow = ({
    backgroundImageUrl,
    cardBottom,
    footer,
    className = '',
    children,
}: SurveyWindowProps) => {
    const blurredImageStyle = backgroundImageUrl
        ? {
              backgroundImage: `url('${backgroundImageUrl}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              filter: `blur(${BACKGROUND_BLUR_PX}px)`,
              transform: `scale(${BACKGROUND_BLUR_SCALE})`,
          }
        : undefined;

    const gradientOverlayStyle = backgroundImageUrl
        ? { backgroundImage: BACKGROUND_GRADIENT_OVERLAY }
        : undefined;

    return (
        <div
            className={`rounded-[40px] border border-white bg-white/45 shadow-[0_45px_120px_rgba(65,36,118,0.2)] backdrop-blur-sm ${className}`}
        >
            <div className={'survey-container-padding'}>
                <div
                    className={
                        'relative w-full overflow-hidden rounded-[30px] shadow-[0_35px_80px_rgba(0,0,0,0.45)]'
                    }
                    style={{ minHeight: MIN_CONTENT_HEIGHT_PX }}
                >
                    {backgroundImageUrl && (
                        <>
                            <div
                                aria-hidden
                                className={'absolute inset-0'}
                                style={blurredImageStyle}
                            />

                            <div
                                aria-hidden
                                className={'absolute inset-0'}
                                style={gradientOverlayStyle}
                            />
                        </>
                    )}

                    {children}

                    {cardBottom}
                </div>

                {footer}
            </div>
        </div>
    );
};

/** What fills the dark card, centred, clear of what is laid over its bottom edge. */
export const SurveyWindowContent = ({ children }: PropsWithChildren) => (
    <div
        className={'relative flex flex-col items-center justify-center px-6 py-20 sm:px-12'}
        style={{ minHeight: MIN_CONTENT_HEIGHT_PX }}
    >
        {children}
    </div>
);

/** The same height, for content that fills the dark card edge to edge. */
export const SurveyWindowSection = ({ children }: PropsWithChildren) => (
    <div
        className={'relative flex flex-1 items-center justify-center text-white'}
        style={{ minHeight: MIN_CONTENT_HEIGHT_PX }}
    >
        {children}
    </div>
);
